import * as Tooltip from '~/components/ui/Tooltip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shadcn/components/ui/table';
import { useGetLogs } from '@shadcn/hooks/use-logs';
import { getEdgeFunctionLogsQuery, LogsTableName } from '@shadcn/lib/logs';
import { Alert } from '~/components/ui/Alert';
import { Skeleton } from '~/components/ui/Skeleton';
import { usePaginatedData } from '~/lib/hooks/usePaginatedData';
import { Pagination } from '~/components/ui/Pagination';
import { useMemo, useState } from 'react';
import { IconButton } from '~/components/ui/IconButton';
import { SortIndicator } from '~/components/ui/SortIndicator';
import { LogView } from './LogView.client';
import { Button } from '~/components/ui/Button';

const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_LOGS_LIMIT = 100;
const DEFAULT_SORT_FIELD = 'timestamp';
const DEFAULT_SORT_DIRECTION: 'ASC' | 'DESC' = 'DESC';

export function EdgeFunctionsLogs({ name, id, onBack }: { name: string; id: string; onBack: () => void }) {
  const sql = useMemo(
    () => getEdgeFunctionLogsQuery(id, DEFAULT_LOGS_LIMIT, DEFAULT_SORT_FIELD, DEFAULT_SORT_DIRECTION),
    [id],
  );
  const { data, isLoading, error } = useGetLogs({ sql });
  const list = useMemo(() => data?.result ?? [], [data]);
  const [selectedLogId, setSelectedLogId] = useState<null | string>(null);

  const {
    data: paginatedLogs,
    page,
    onPageChange,
    totalPages,
  } = usePaginatedData(list, {
    pageSize: DEFAULT_PAGE_SIZE,
  });

  if (selectedLogId) {
    return (
      <LogView
        id={selectedLogId}
        tableName={LogsTableName.FUNCTIONS}
        onBack={() => {
          setSelectedLogId(null);
        }}
      />
    );
  }

  const tableCellClassName =
    'first:pl-6 lg:first:pl-8 last:pr-6 lg:last:pr-8 text-xs text-bolt-ds-textPrimary group-hover:text-bolt-ds-textPrimary min-w-[8rem]';

  return (
    <div className="pt-12">
      <div className="flex items-center gap-2 text-bolt-ds-textPrimary mb-4">
        <IconButton variant="link" icon="i-heroicons:arrow-left size-4 mb-0.5" onClick={onBack} size="sm" />
        <h2 className="text-lg font-semibold">Logs for {name} Server Function</h2>
      </div>
      <p className="hidden lg:block text-sm lg:text-base text-bolt-ds-textSecondary mb-6">
        View server function execution logs and events. Showing activity from the past hour only.
      </p>

      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
        </div>
      )}
      {!isLoading && error && (
        <Alert type="danger" title="Error fetching logs">
          {error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.'}
        </Alert>
      )}
      {!isLoading && paginatedLogs.length > 0 && (
        <div className="flex flex-col gap-4">
          <Pagination
            page={page}
            allowPageInput
            descriptionNode={`Showing up to ${DEFAULT_LOGS_LIMIT} records`}
            pageSize={DEFAULT_PAGE_SIZE}
            totalItems={list.length}
            totalPages={totalPages}
            onPageChange={onPageChange}
            className="rounded-b-[0px] mb-[-16px]"
          />
          <Table className="border border-bolt-ds-borderOutline rounded-md">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {Object.keys(paginatedLogs[0] as object)
                  .filter((key) => key !== 'function_id')
                  .map((key, idx, arr) => (
                    <TableHead
                      key={key}
                      className={
                        (idx === 0 ? 'first:pl-6 lg:first:pl-8 ' : '') +
                        (idx === arr.length - 1 ? 'last:pr-6 lg:last:pr-8 ' : '')
                      }
                    >
                      <div className="flex items-center gap-1">
                        <span>{key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}</span>
                        <SortIndicator
                          activeField={key}
                          sortField={DEFAULT_SORT_FIELD}
                          sortDirection={DEFAULT_SORT_DIRECTION}
                        />
                      </div>
                    </TableHead>
                  ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {(paginatedLogs as any[]).map((log, index) => (
                <TableRow key={log.id || index} className="group hover:bg-bolt-ds-surfaceThree/50 relative">
                  {Object.keys(paginatedLogs[0] ?? [])
                    .filter((key) => key !== 'function_id')
                    .map((key) => {
                      const value = log[key];

                      const formattedValue = (() => {
                        if (key === 'timestamp' && typeof value === 'number') {
                          return new Date(value / 1000).toLocaleString();
                        }

                        if (value === null) {
                          return 'NULL';
                        }

                        return typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
                      })();

                      return (
                        <TableCell key={key} className={tableCellClassName}>
                          <Tooltip.Root delayDuration={300}>
                            <Tooltip.Trigger asChild>
                              <div className="text-xs font-mono w-fit max-w-96 truncate cursor-default">
                                {formattedValue}
                              </div>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                              <Tooltip.Content sideOffset={4} align="start" side="top">
                                <div className="w-96 max-h-96 p-3 z-max">
                                  <pre className="text-xs font-mono whitespace-pre-wrap break-words">
                                    {formattedValue}
                                  </pre>
                                </div>
                              </Tooltip.Content>
                            </Tooltip.Portal>
                          </Tooltip.Root>
                        </TableCell>
                      );
                    })}
                  <TableCell className={tableCellClassName}>
                    <Button
                      startIcon="i-heroicons:document-text"
                      size="md"
                      variant="secondary"
                      onClick={() => {
                        setSelectedLogId(log.id);
                      }}
                    >
                      View Log Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {!isLoading && !error && paginatedLogs.length === 0 && (
        <div className="text-bolt-ds-textTertiary">
          No logs for <span className="font-semibold">{name}</span> Server Function available yet.
        </div>
      )}
    </div>
  );
}
